import { useCallback, useEffect, useState } from 'react';
import {
  aiForecastApi,
  type AgentDefinition,
  type AIForecastDashboard,
  type AIForecastRecord,
  type Explanation,
  type ForecastComparison,
  type Recommendation,
  type ScenarioResult,
} from '../../api/aiforecast';

type Tab = 'dashboard' | 'recommendations' | 'confidence' | 'comparison' | 'explanations' | 'scenarios' | 'agents' | 'learning';
type Theme = 'light' | 'dark';

function fmtNum(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function riskColor(level: string, dark: boolean) {
  switch (level) {
    case 'critical':
    case 'high':
      return dark ? 'text-red-400' : 'text-red-600';
    case 'moderate':
      return dark ? 'text-amber-400' : 'text-amber-600';
    default:
      return dark ? 'text-emerald-400' : 'text-emerald-600';
  }
}

export function AIForecastPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<AIForecastDashboard | null>(null);
  const [forecast, setForecast] = useState<AIForecastRecord | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [explanation, setExplanation] = useState<Explanation | null>(null);
  const [comparison, setComparison] = useState<ForecastComparison | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioResult[]>([]);
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, forecasts, agentList] = await Promise.all([
        aiForecastApi.dashboard(),
        aiForecastApi.listForecasts(),
        aiForecastApi.listAgents(),
      ]);
      setDashboard(dash);
      setAgents(agentList);
      const fc = forecasts[0] ?? null;
      setForecast(fc);
      if (!fc) return;

      const [recs, exps, cmp, sc] = await Promise.all([
        aiForecastApi.listRecommendations(fc.id),
        aiForecastApi.listExplanations(fc.id),
        aiForecastApi.getComparison(fc.id),
        aiForecastApi.listScenarios(fc.id),
      ]);
      setRecommendations(recs);
      setExplanation(exps[0] ?? null);
      setComparison(cmp);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load AI forecast intelligence');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAccept = async (rec: Recommendation) => {
    if (!forecast) return;
    setBusy(true);
    try {
      await aiForecastApi.submitFeedback({
        ai_forecast_id: forecast.id,
        recommendation_id: rec.id,
        action: 'accepted',
        planner_notes: 'Accepted via dashboard',
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Feedback failed');
    } finally {
      setBusy(false);
    }
  };

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'confidence', label: 'Confidence Explorer' },
    { id: 'comparison', label: 'Forecast Comparison' },
    { id: 'explanations', label: 'Business Explanation' },
    { id: 'scenarios', label: 'Scenario Explorer' },
    { id: 'agents', label: 'Multi-Agent View' },
    { id: 'learning', label: 'Learning Dashboard' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">AI Forecast Intelligence Engine</h1>
            <p className={`mt-1 text-sm ${muted}`}>
              Multi-agent reasoning layer — augments statistical forecasts with explainable recommendations
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTheme(dark ? 'light' : 'dark')}
              className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}
              aria-label="Toggle dark mode"
            >
              {dark ? 'Light' : 'Dark'}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>
        {forecast && (
          <p className={`mt-2 text-sm ${muted}`}>
            {forecast.name} · Statistical {fmtNum(forecast.statistical_units)} → AI {fmtNum(forecast.ai_forecast_units)} ·{' '}
            Confidence {(forecast.confidence_score * 100).toFixed(0)}% ·{' '}
            <span className={riskColor(forecast.risk_level, dark)}>Risk: {forecast.risk_level}</span>
          </p>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">
          {error}
        </div>
      )}

      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist" aria-label="AI Forecast sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${
                tab === t.id ? 'border-violet-600 text-violet-600' : 'border-transparent text-slate-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active AI Forecasts', value: dashboard.active_forecasts },
              { label: 'Recommendations', value: dashboard.total_recommendations },
              { label: 'Avg Confidence', value: `${(dashboard.avg_confidence * 100).toFixed(0)}%` },
              { label: 'Opportunity Score', value: dashboard.avg_opportunity_score.toFixed(0) },
              { label: 'High Risk Items', value: dashboard.high_risk_count },
              { label: 'Pending Actions', value: dashboard.pending_recommendations },
              { label: 'Accuracy Improvement', value: `+${dashboard.forecast_accuracy_improvement.toFixed(1)}%` },
              { label: 'Active Agents', value: dashboard.agent_count },
            ].map((m) => (
              <div key={m.label} className={card}>
                <p className={`text-sm ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'recommendations' && (
          <div className="space-y-4">
            {recommendations.map((rec) => (
              <article key={rec.id} className={card}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <span className="rounded bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900 dark:text-violet-300">
                      {rec.category.replace(/_/g, ' ')}
                    </span>
                    <h3 className="mt-2 text-lg font-medium">{rec.title}</h3>
                    <p className={`mt-1 text-sm ${muted}`}>{rec.description}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p>Confidence {(rec.confidence * 100).toFixed(0)}%</p>
                    <p className={muted}>Priority {rec.priority}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm"><strong>Reasoning:</strong> {rec.reasoning_summary}</p>
                <ul className={`mt-2 list-inside list-disc text-sm ${muted}`}>
                  {rec.supporting_evidence.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
                <p className="mt-2 text-sm"><strong>Expected impact:</strong> {rec.expected_impact}</p>
                {rec.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => handleAccept(rec)}
                    disabled={busy}
                    className="mt-3 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Accept Recommendation
                  </button>
                )}
                {rec.status !== 'pending' && (
                  <span className="mt-3 inline-block rounded bg-slate-200 px-2 py-1 text-xs dark:bg-slate-700">{rec.status}</span>
                )}
              </article>
            ))}
          </div>
        )}

        {tab === 'confidence' && forecast && (
          <div className={`${card} max-w-2xl`}>
            <h2 className="text-lg font-medium">Confidence Explorer</h2>
            <div className="mt-4 space-y-3">
              <div>
                <div className="flex justify-between text-sm">
                  <span>AI Confidence</span>
                  <span>{(forecast.confidence_score * 100).toFixed(1)}%</span>
                </div>
                <div className="mt-1 h-3 rounded-full bg-slate-200 dark:bg-slate-700">
                  <div className="h-3 rounded-full bg-violet-600" style={{ width: `${forecast.confidence_score * 100}%` }} />
                </div>
              </div>
              <p className={`text-sm ${muted}`}>
                Range: {fmtNum(forecast.confidence_low)} – {fmtNum(forecast.confidence_high)} units
              </p>
              <p className={`text-sm ${muted}`}>
                Statistical baseline confidence: {(forecast.statistical_confidence * 100).toFixed(0)}%
              </p>
              <p className={`text-sm ${muted}`}>
                Opportunity score: {forecast.opportunity_score.toFixed(0)} / 100
              </p>
            </div>
          </div>
        )}

        {tab === 'comparison' && comparison && (
          <div className={card}>
            <h2 className="text-lg font-medium">Statistical vs AI Forecast</h2>
            <p className={`mt-2 text-sm ${muted}`}>{comparison.comparison_summary}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <p className={`text-sm ${muted}`}>Statistical</p>
                <p className="text-xl font-semibold">{fmtNum(comparison.statistical_units)}</p>
                <p className={`text-xs ${muted}`}>{comparison.statistical_algorithm?.replace(/_/g, ' ') ?? 'baseline'}</p>
              </div>
              <div>
                <p className={`text-sm ${muted}`}>AI Forecast</p>
                <p className="text-xl font-semibold">{fmtNum(comparison.ai_forecast_units)}</p>
                <p className={`text-xs ${muted}`}>Deviation {comparison.deviation_pct > 0 ? '+' : ''}{comparison.deviation_pct.toFixed(1)}%</p>
              </div>
              <div>
                <p className={`text-sm ${muted}`}>Expected Accuracy</p>
                <p className="text-xl font-semibold">{comparison.expected_accuracy.toFixed(1)}%</p>
                <p className={`text-xs ${muted}`}>Improvement +{comparison.improvement_score.toFixed(1)} pts</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'explanations' && explanation && (
          <div className={`${card} space-y-4`}>
            <h2 className="text-lg font-medium">Business Explanation</h2>
            <p>{explanation.reasoning_summary}</p>
            <div>
              <h3 className="font-medium">Statistical Comparison</h3>
              <p className={`text-sm ${muted}`}>{explanation.statistical_comparison}</p>
            </div>
            <div>
              <h3 className="font-medium">Supporting Evidence</h3>
              <ul className={`list-inside list-disc text-sm ${muted}`}>
                {explanation.supporting_evidence.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-medium">Known Risks</h3>
              <ul className={`list-inside list-disc text-sm ${muted}`}>
                {explanation.known_risks.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-medium">Expected Outcome</h3>
              <p className={`text-sm ${muted}`}>{explanation.expected_business_outcome}</p>
            </div>
          </div>
        )}

        {tab === 'scenarios' && (
          <div className="grid gap-4 md:grid-cols-3">
            {scenarios.map((sc) => (
              <div key={sc.id} className={card}>
                <span className="rounded bg-slate-200 px-2 py-0.5 text-xs dark:bg-slate-700">{sc.case.replace(/_/g, ' ')}</span>
                <h3 className="mt-2 font-medium">{sc.name}</h3>
                <p className={`mt-1 text-sm ${muted}`}>{sc.description}</p>
                <p className="mt-3 text-xl font-semibold">{fmtNum(sc.forecast_units)} units</p>
                <p className={`text-sm ${muted}`}>Revenue ${fmtNum(sc.revenue_impact)} · Confidence {(sc.confidence * 100).toFixed(0)}%</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'agents' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {agents.map((a) => (
              <div key={a.type} className={card}>
                <h3 className="font-medium">{a.label}</h3>
                <p className={`mt-1 text-sm ${muted}`}>{a.responsibility}</p>
                <p className="mt-2 text-xs"><strong>Escalation:</strong> {a.escalation_rule}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'learning' && dashboard && (
          <div className={`${card} max-w-xl`}>
            <h2 className="text-lg font-medium">Continuous Learning</h2>
            <div className="mt-4 space-y-2 text-sm">
              <p>Learning events captured: <strong>{dashboard.learning_events}</strong></p>
              <p>Planner acceptance rate: <strong>{dashboard.planner_acceptance_rate.toFixed(0)}%</strong></p>
              <p>Forecast accuracy improvement: <strong>+{dashboard.forecast_accuracy_improvement.toFixed(1)}%</strong></p>
              <p className={`mt-4 ${muted}`}>
                Submit feedback on recommendations to improve future AI forecast intelligence. No recommendations execute automatically.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
